import React, { useEffect, useState } from 'react';
import { Form, Col, Row, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Button from "@material-ui/core/Button";
import { Slider, CircularProgress, createTheme, ThemeProvider } from "@material-ui/core";
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import SvgIcon from '@material-ui/core/SvgIcon';
import NumberFormat from 'react-number-format';
import { makeStyles, withStyles } from "@material-ui/core/styles";
import DigitalCoverageAmounts from '../../../Helpers/Lists/CoverageAmounts/DigitalCoverageAmounts';
import ProductCoverageLength from '../../../Helpers/Lists/CoverageLengths/ProductCoverageLength';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import TobaccoUsage from '../../Common/Helpers/TobaccoUsage';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import ShieldIcon from '../../../../icons/shield.svg';
import TimeIcon from '../../../../icons/time.svg';
import DollarIMacIcon from '../../../../icons/dollar-imac.svg';
import { SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_TERM_STRUCTURE, getSessionStorageItem, getBasicInfo, setBasicInfo, setSessionStorageItem } from '../../../../sessionStorage';
import Age from '../../../Helpers/Dates/Age';
import { CheckboxInput } from "../../../Helpers/Inputs";
import IsMultiTerm from "../../Common/Helpers/IsMultiTerm";
import { alpha } from '@material-ui/core/styles';
import './PreQuote.scss';
import NeedsAnalysisButton from '../../../Tools/NeedsAnalysis/NeedsAnalysisButton';
import QuoteBasicInfo from '../../../Quoters/Common/QuoteBasicInfo';
import GetProductTypeEnum from '../../../../types/productType';
import CustomerSupportPhone from '../../../Account/CustomerSupportPhone';
import CustomerSupportEmail from '../../../Account/CustomerSupportEmail';

const LightTooltip = withStyles((theme) => ({
  arrow: {
    color: theme.palette.common.white,
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[4],
    fontSize: 12,
    maxWidth: 385,
  },
  tooltipPlacementTop: {
    [theme.breakpoints.up('xs')]: {
      marginBottom: 5,
      marginRight: 0,
    },
    [theme.breakpoints.up('sm')]: {
      marginBottom: 5,
      marginRight: 140,
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: 5,
      marginRight: 0,
    },
    [theme.breakpoints.up('xl')]: {
      marginBottom: 5,
      marginRight: 190,
    },
    [theme.breakpoints.between(992, 1199)]: {
      marginBottom: 5,
      marginRight: 110,
    },
    [theme.breakpoints.between(1200, 1280)]: {
      marginBottom: 5,
      marginRight: 120,
    },
  },
}))(Tooltip);

const useStyles = makeStyles(theme => ({
  cardTitle: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: "12px",
    margin: "5px 0 0 0",
  },
  backButton: {
    color: theme.palette.primary.main,
    background: "white"
  },
  rate: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '3rem'
  },
  amount: {
    color: theme.palette.primary.main
  },
  descBackground: {
    backgroundColor: alpha(theme.palette.primary.main, .1)
  },
  lightBackground: {
    backgroundColor: `${theme.palette.primary.main}16`,
  },
}));

const quoterTheme = {
  typography: {
    fontFamily: "museo-sans",
    body1: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  overrides: {
    MuiFormControl: {
      root: {
        "&.coverage-amount": {
          display: "flex",
          alignItems: "center",
        },
        "&.coverage-amount .coverage-input": {
          display: "flex",
          width: 188,
          height: 47,
          alignItems: "center",
          borderRadius: 6,

        },
        "&.coverage-amount .coverage-input::before": {
          content: "none",
        },
        "&.coverage-amount .coverage-input::after": {
          content: "none",
        },
        "&.coverage-amount .coverage-input input": {
          padding: 0,
        },
      },
    },

    MuiInputBase: {
      input: {
        fontSize: 16,
      },
      root: {
        height: 56,
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
      underline: {
        "&::before": {
          borderBottom: "solid 2px #b2b2b2",
        },
        "&:hover::before": {
          borderBottomWidth: "2px",
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 16,
        color: "rgba(0,0,0,0.6)",
      }
    },
    MuiFormHelperText: {
      root: {
        marginTop: 0,
        paddingTop: 0,
      },
    },
    MuiInputLabel: {
      shrink: {
        color: "rgba(0,0,0,0.3) !important",
      },
    },
    MuiSelect: {
      fontSize: 16,
      icon: {
        color: "rgba(0,0,0,1)"
      },
      iconFilled: {
        right: 16,
      },
    },
    MuiSlider: {
      root: {
        height: 4,
        margin: "8px 0",
        padding: 0,
      },
      rail: {
        height: 4,
        borderRadius: 2,
        backgroundColor: " #ccc",
        opacity: 1,
      },
      track: {
        height: 4,
        borderRadius: 2,
      },
      mark: {
        display: "none",
      },
      thumb: {
        marginTop: -4,
      },
      markLabel: {
        top: 9,
        fontSize: 12,
        color: "#333",
        "&[style*='0%;']": {
          "left": "0px !important",
          "transform": "translateX(0)",
        },
        "&[style*='100%;']": {
          "right": "0px !important",
          "left": "auto !important",
          "transform": "translateX(0)",
        },
      },
    },
    MuiSvgIcon: {
      root: {
        width: 24,
        height: 24,
        marginBottom: 1,
      },
      colorAction: {
        fill: "#b2b2b2",
      },
    },
    MuiPaper: {
      outlined: {
        border: "none",
        boxShadow: "0 2px 3px rgba(0, 0, 0, 0.161)",
      },
    },
  },
};

export default function PreQuote(props) {
  const basicInfoDetails = getBasicInfo();
  const [loading, setLoading] = useState(true);
  const [quoteRan, setQuoteRan] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [multiTermResult, setMultiTermResult] = useState(false);
  const [coverageAmount, setCoverageAmount] = useState(basicInfoDetails.faceAmount || parseInt(props.formState.coverageAmountQuote, 10));
  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;
  var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const termAccountProduct = accountObj && accountObj.accountProducts.find(e => e.productTypeId === GetProductTypeEnum().FullUW);
  const bestowHandoffAccount = termAccountProduct && termAccountProduct.bestowHandoffAccount;
  const { handleSubmit } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  
  const classes = useStyles();

  useEffect(() => {
    props.onMultiTerm(multiTermResult);
    setSessionStorageItem(SESSION_STORAGE_TERM_STRUCTURE, {multiTerm: multiTermResult})
  }, [multiTermResult])

  const runQuote = (getIsSubscribed) => {

    const feet = props.basicInfo.height == null || props.basicInfo.height === '' ? 0 : props.basicInfo.height.split('ft')[0].trim();
    const inches = props.basicInfo.height == null || props.basicInfo.height === '' ? 0 : props.basicInfo.height.split('ft')[1].split('in')[0].trim();
    const weight = props.basicInfo.weight == null || props.basicInfo.weight === '' ? 0 : props.basicInfo.weight.split(' ')[0];
    const tobacco = TobaccoUsage(props.basicInfo.tobacco);
    setQuoteRan(true);

    fetch(`${process.env.REACT_APP_COVR_SERVICES_URL}/api/VSS/GetCustomHealthClass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ aboutMe: props.basicInfo }),
    })
      .then(res => res.json())
      .then(customHealthClassResponse => {
        const queryParams = {
          birthdate: props.basicInfo.birthdate,
          state: props.basicInfo.state,
          gender: props.basicInfo.gender,
          faceAmount: coverageAmount,
          tobacco: tobacco,
          healthClass: customHealthClassResponse.defaultHealthClass,
          term: props.formState.termQuote,
          heightFt: feet,
          heightIn: inches,
          weight: weight,
          oneClickAccountId: accountObj.account.oneClickAccountId,
          replacePolicy: props.formState.replacePolicy,
          noKnockouts: customHealthClassResponse.isQualifiedForDigital,
          sessionId: accountObj.sessionId,
          isLevelTerm: true,
          isDecreasingTerm: true,
        };

        if (bestowHandoffAccount) {
          queryParams.carrierData = [
            {
              carrier: 'bestow',
              product: 'bestow_digital',
              items: {
                "short_url_id": bestowHandoffAccount,
              },
            }
          ];
        }

        fetch(`${covrServicesUrl}/api/MCT/GetMCTQuote`, {
          method: 'POST',
          headers: {
            'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(queryParams),
        })
          .then((res) => res.json())
          .then((data) => {
            if (getIsSubscribed()) {
              var quoteResults = data && data.quoteResults;
              if (quoteResults && quoteResults.length) {

                setMultiTermResult(IsMultiTerm(data));
                setQuoteResults(quoteResults.sort((a, b) => a.monthlyRate - b.monthlyRate || b.totalUnweightedScore - a.totalUnweightedScore)[0]);

                // save off QuoteLogId here.............
               
              } else {
                setQuoteResults([]);
              }
              setLoading(false);
            }
          })
          .catch((error) => {
            if (getIsSubscribed()) {
              ErrorHandler(error, setQuoteResults);
              setLoading(false);
            }
          });
      });
  };

  let debounce = null;
  useEffect(() => {
    if (coverageAmount == null || props.formState.termQuote == null) {
      return;
    }

    setLoading(true);

    let isSubscribed = true;

    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    };

  }, [coverageAmount, props.formState.termQuote]);

  const onSubmit = () => {
    const basicInfo = getBasicInfo();
    basicInfo.faceAmount = coverageAmount
    basicInfo.termLength = props.formState.termQuote;
    setBasicInfo(basicInfo);
    var form = { ...props.fullFormState };
    form.pages.preQuote.coverageAmountQuote = coverageAmount
    props.onMultiFieldChange(form);
    props.onNext()
  }
  const [preQuoteTooltip, setPreQuoteTooltip] = useState(false);
  const handleTooltipPreQuoteClose = () => {
    setPreQuoteTooltip(false);
  };
  const handleTooltipPreQuoteOpen = () => {
    setPreQuoteTooltip(true);
  };

  useEffect(() => {
    // dynamic term length based on age

    const age = Age(props.basicInfo.birthdate);
    if (age <= 45) {
      props.onFieldChange('termQuote', 30);
    } else if (age <= 55) {
      props.onFieldChange('termQuote', 20);
    } else {
      props.onFieldChange('termQuote', 10);
    }
  }, [])

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...quoterTheme,
    })}>
      <Container className="p-0 outer-div" >
        <div className="MCT-info d-flex justify-content-center inner-div" >
          <div className="pre-quote-form">
            <Row className="pre-quote-container">
              <Col lg={8} xs={12} sm={12} md={12}>
                <div className="bg-white block">
                  <Row className="text-center">
                    <Col>
                      <div className="d-flex justify-content-center">
                        <div className={`icon-container ${classes.lightBackground}`}>
                          <SvgIcon component={ShieldIcon} color='primary' />
                        </div>
                      </div>
                      <div className="card-title">Select Coverage Amount</div>
                      <Form.Group className='amount-div'>

                        <div className='text-decor pt-0 pb-0'>

                          <NumberFormat
                            className="quote-amount-dollars"
                            value={coverageAmount}
                            displayType={'text'}
                            thousandSeparator={true}
                            decimalScale={0}
                            fixedDecimalScale={true}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="coverage-slider">
                        <Slider
                          min={100000}
                          max={3000000}
                          step={50000}
                          value={coverageAmount}
                          onChange={(e, v) => {props.onFieldChange("coverageAmountQuote", v); setCoverageAmount(v)}}
                          className="coverage-amount"
                          marks={DigitalCoverageAmounts(false)}
                          role="slider"
                          aria-labelledby="coverageAmountQuote"
                        />
                      </Form.Group>

                      <p className={`tag-line ${classes.descBackground}`}>
                        Most financial experts recommend 10-15x your income plus your mortgage balance.
                      </p>
                      <div className="pt-2">
                        <NeedsAnalysisButton
                          isPrequote={true}
                          title="HOW MUCH I NEED"
                          setCoverageAmount={(face) => {props.onFieldChange("coverageAmountQuote", face); setCoverageAmount(face)}}
                          type="quote-screen"
                        />
                      </div>
                    </Col>
                    <Col>
                      <div className="d-flex justify-content-center">
                        <div className={`icon-container ${classes.lightBackground}`}>
                          <SvgIcon component={TimeIcon} color='primary' />
                        </div>
                      </div>
                      <div className="card-title">Years of Coverage</div>
                      <Form.Group className="term-slider">
                        <div className='text-decor pt-0 pb-0'

                        ><span>{props.formState.termQuote}</span>
                          <span className='ml-1 inner-span'>Years</span>
                        </div>
                      </Form.Group>

                      <Form.Group className="coverage-slider">
                        <Slider
                          min={10}
                          max={30}
                          step={5}
                          value={props.formState.termQuote}
                          onChange={(e, v) => props.onFieldChange("termQuote", v)}
                          className="coverage-amount"
                          marks={ProductCoverageLength()}
                          role="slider"
                          aria-labelledby="termQuote"
                        />
                      </Form.Group>
                      <p className={`tag-line ${classes.descBackground}`}>Choose a term length that will cover your longest financial commitment, such as a mortgage or loan.</p>
                    </Col>
                  </Row>

                </div>
              </Col>
              {loading ?
                <Col lg={4} xs={12} sm={12} md={12}>
                  <div className="loader-container">
                    <CircularProgress />
                  </div>
                </Col>
                :
                <Col lg={4} xs={12} sm={12} md={12} className="cursor-pointer">
                  <div className="bg-white block quote-rate">
                    <Row className="text-center">
                      <Col>
                        <div className="d-flex justify-content-center">
                          <div className={`icon-container ${classes.lightBackground}`}>
                            <SvgIcon component={DollarIMacIcon} color='primary' />
                          </div>
                        </div>

                        {quoteResults.monthlyRate !== undefined && (
                          <React.Fragment>
                            <div className="d-flex justify-content-center quote-amount-title">
                              <p className="card-title mb-0">Rates as low as
                                <Tooltip title="You’re unique. Based on your information, such as height, weight, and nicotine usage, this estimates how little you could pay per month with the best health rating. This rate is subject to change based on the results of your application process. To get a more accurate quote, candidly answer the following questions." className="toolInfo" role="tooltip" id="preQuote-tooltip" aria-describedby="preQuote-tooltip" aria-label="more about sort by price" >
                                  <LightTooltip
                                    enterTouchDelay={0}
                                    leaveTouchDelay={5000}
                                    onClose={() => handleTooltipPreQuoteClose()}
                                    open={preQuoteTooltip}
                                    placement="top"
                                    arrow
                                  >
                                    <button type='button' id="preQuote" role="tooltip" className="quote-tooltip" onMouseOver={() => handleTooltipPreQuoteOpen()} onClick={() => handleTooltipPreQuoteOpen()} tabIndex="0" aria-disabled="true" aria-describedby="preQuote"><InfoIcon color="action"></InfoIcon></button>
                                  </LightTooltip>
                                </Tooltip>
                              </p>
                              {/* <span>

                        </span> */}
                            </div>
                            <div className="quote-amount-dollars-container">
                              <NumberFormat
                                className={`quote-amount-dollars-inner ${classes.amount}`}
                                value={quoteResults.monthlyRate || 'No quotes available'}
                                displayType={'text'}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                              <span className={`quote-amount-mo ${classes.amount}`}> {quoteResults.monthlyRate != undefined ? '/mo' : ''}</span>
                            </div>
                          </React.Fragment>
                        )}
                      </Col>
                    </Row>


                  { quoteResults.monthlyRate === undefined ?  (
                      <div className={`mt-3 ${classes.descBackground}`}>
                        <p className='tag-line phone-email-text'>We’d like to talk to you about your coverage requirement to make sure the product solutions will meet all of your needs. Contact one of our licensed experts to discuss your options.</p>
                        <p className='tag-line'>Email us at <a href={`mailto:${CustomerSupportEmail()}`}>{CustomerSupportEmail()}</a></p>
                        <p className='tag-line'>Please call us at <a href={`tel:${CustomerSupportPhone()}`}>{CustomerSupportPhone()}</a></p>
                      </div>
                    ) : (
                      <React.Fragment>
                        <div className="no-content"></div>
                        <p className={`tag-line ${classes.descBackground}`}>
                          Answer a few more questions to see the policy options that best fit your needs.
                        </p>
                      </React.Fragment>
                    )
                    }

                    <div className="no-content"></div>
                  </div>
                </Col>
              }
            </Row>
            <Row className='justify-content-center replace-policy-container'>
              <Form.Group>
                <CheckboxInput
                  name="replacePolicy"
                  id="replacePolicy"
                  checked={props.formState.replacePolicy}
                  onChange={e => props.onFieldChange('replacePolicy', e.target.checked)}
                  label="This coverage will replace an existing policy"
                />
              </Form.Group>
            </Row>

            <Row className="pre-quote-nav-control">
              <Col className="text-left btn-back">
                <Button variant="outlined" color="primary" className={classes.backButton} size="medium" onClick={props.onBack}>BACK</Button>
              </Col>
              <Col className="text-right">
                <Button variant="contained" disabled={loading || quoteResults.monthlyRate === undefined} size="medium" onClick={onSubmit} color="primary" >NEXT</Button>
              </Col>
            </Row>
          </div>
        </div>
        <QuoteBasicInfo {...props} />
      </Container>
    </ThemeProvider>
  );
}

PreQuote.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onMultiTerm: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

PreQuote.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    coverageAmountQuote: prefill ? 500000 : (basicInfo && basicInfo.faceAmount) || 500000,
    termQuote: prefill ? '20' : (basicInfo && basicInfo.termLength) || '20',
    replacePolicy: prefill ? true : false
  }
};



// WEBPACK FOOTER //
// ./src/components/Quoters/MCT/steps/PreQuote.js